import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { LicenseFormData } from "../hooks/useLicenseForm";
import type { ThemeClasses } from "../types";
import {
  AVAILABLE_APPS,
  ENTITY_LIMIT_KEYS,
  BILLING_MODELS,
  BILLING_CYCLES,
  CURRENCIES,
  STATUS_OPTIONS,
} from "../types";

interface LicenseAddModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  form: UseFormReturn<LicenseFormData>;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (data: LicenseFormData) => Promise<void>;
  isEdit: boolean;
  hasFormChanges: () => boolean;
  editLicenseId: string | null;
}

export default function LicenseAddModal({
  themeClasses,
  isDarkMode,
  form,
  loadingSubmit,
  onClose,
  onSubmit,
  isEdit,
  hasFormChanges,
}: LicenseAddModalProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const [newFeature, setNewFeature] = useState("");
  const billingModel = watch("billing_model");
  const selectedApps = watch("apps");
  const features = watch("features");

  const toggleApp = (appId: string) => {
    const current = selectedApps || [];
    if (current.includes(appId)) {
      setValue("apps", current.filter((a) => a !== appId), { shouldDirty: true });
    } else {
      setValue("apps", [...current, appId], { shouldDirty: true });
    }
  };

  const addFeature = () => {
    const trimmed = newFeature.trim();
    if (trimmed && !features.includes(trimmed)) {
      setValue("features", [...features, trimmed], { shouldDirty: true });
      setNewFeature("");
    }
  };

  const removeFeature = (idx: number) => {
    setValue(
      "features",
      features.filter((_, i) => i !== idx),
      { shouldDirty: true },
    );
  };

  const handleEntityLimitChange = (key: string, value: string) => {
    const numVal = value === "" ? null : parseInt(value, 10);
    setValue(`entity_limits.${key as keyof LicenseFormData["entity_limits"]}`, numVal as number | null, {
      shouldDirty: true,
    });
  };

  const inputClass = `w-full px-3 py-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50`;
  const labelClass = `block text-sm font-medium ${themeClasses.text} mb-1`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl ${themeClasses.bg} shadow-2xl`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${themeClasses.border} ${themeClasses.bg}`}>
          <h2 className={`text-lg font-bold ${themeClasses.text}`}>
            <i className="ri-key-2-line mr-2"></i>
            {isEdit ? "Editar Licencia" : "Nueva Licencia"}
          </h2>
          <button aria-label="Cerrar" onClick={onClose} className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText}`}>
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* === BASIC INFO === */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3 flex items-center gap-1`}>
              <i className="ri-information-line"></i> Información básica
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Name */}
              <div className="sm:col-span-2">
                <label className={labelClass}>
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("name", { required: "El nombre es requerido" })}
                  className={inputClass}
                  placeholder="Ej: Licencia Básica"
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                )}
              </div>

              {/* Description */}
              <div className="sm:col-span-2">
                <label className={labelClass}>Descripción</label>
                <textarea
                  {...register("description")}
                  className={`${inputClass} resize-none`}
                  rows={2}
                  placeholder="Descripción de la licencia..."
                />
              </div>

              {/* Status */}
              <div>
                <label className={labelClass}>Estado</label>
                <select {...register("status")} className={inputClass}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className={labelClass}>Notas internas</label>
                <input
                  {...register("notes")}
                  className={inputClass}
                  placeholder="Notas..."
                />
              </div>
            </div>
          </div>

          {/* === BILLING === */}
          <div className={`p-4 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
            <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3 flex items-center gap-1`}>
              <i className="ri-money-dollar-circle-line"></i> Facturación
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Billing model */}
              <div>
                <label className={labelClass}>Modelo de cobro</label>
                <select {...register("billing_model")} className={inputClass}>
                  {BILLING_MODELS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Billing cycle */}
              <div>
                <label className={labelClass}>Ciclo de facturación</label>
                <select {...register("billing_cycle")} className={inputClass}>
                  {BILLING_CYCLES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Price per unit */}
              {billingModel === "per_unit" && (
                <div>
                  <label className={labelClass}>Precio por unidad</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register("price_per_unit", { valueAsNumber: true })}
                    className={inputClass}
                    placeholder="0.00"
                  />
                </div>
              )}

              {/* Flat price */}
              {billingModel === "flat" && (
                <div>
                  <label className={labelClass}>Tarifa fija</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register("flat_price", { valueAsNumber: true })}
                    className={inputClass}
                    placeholder="0.00"
                  />
                </div>
              )}

              {/* Tiered - both prices */}
              {billingModel === "tiered" && (
                <>
                  <div>
                    <label className={labelClass}>Precio por unidad (base)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register("price_per_unit", { valueAsNumber: true })}
                      className={inputClass}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Tarifa fija (base)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register("flat_price", { valueAsNumber: true })}
                      className={inputClass}
                      placeholder="0.00"
                    />
                  </div>
                </>
              )}

              {/* Currency */}
              <div>
                <label className={labelClass}>Moneda</label>
                <select {...register("currency")} className={inputClass}>
                  {CURRENCIES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* === APPS === */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3 flex items-center gap-1`}>
              <i className="ri-apps-line"></i> Apps incluidas
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {AVAILABLE_APPS.map((app) => {
                const isSelected = selectedApps?.includes(app.id);
                return (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => toggleApp(app.id)}
                    className={`p-3 rounded-xl border text-sm text-left transition-all ${
                      isSelected
                        ? isDarkMode
                          ? "bg-primary-500/20 border-primary-500 text-primary-400"
                          : "bg-primary-50 border-primary-500 text-primary-700"
                        : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} hover:border-primary-300`
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <i className={`ri-${isSelected ? "checkbox-circle-fill text-primary-500" : "checkbox-blank-circle-line " + themeClasses.textSecondary}`}></i>
                      <span className="truncate">{app.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* === FEATURES === */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3 flex items-center gap-1`}>
              <i className="ri-check-double-line"></i> Características incluidas
            </h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addFeature();
                  }
                }}
                className={`${inputClass} flex-1`}
                placeholder="Agregar característica..."
              />
              <button
                aria-label="Agregar"
                type="button"
                onClick={addFeature}
                className="px-4 py-2.5 rounded-xl bg-primary-500 text-gray-900 font-medium hover:bg-primary-600 transition-all text-sm"
              >
                <i className="ri-add-line"></i>
              </button>
            </div>
            {features && features.length > 0 && (
              <div className="space-y-1">
                {features.map((feature, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}
                  >
                    <span className={`text-sm ${themeClasses.text} flex items-center gap-2`}>
                      <i className="ri-check-line text-green-500"></i>
                      {feature}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFeature(idx)}
                      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* === ENTITY LIMITS === */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3 flex items-center gap-1`}>
              <i className="ri-dashboard-line"></i> Límites de entidades
              <span className={`text-xs font-normal ${themeClasses.textSecondary} ml-1`}>(vacío = ilimitado)</span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {ENTITY_LIMIT_KEYS.map(({ key, label, icon }) => {
                const val = watch(`entity_limits.${key as keyof LicenseFormData["entity_limits"]}`);
                return (
                  <div key={key} className={`p-3 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"} text-center`}>
                    <i className={`${icon} text-lg ${isDarkMode ? "text-primary-400" : "text-primary-600"} mb-1 block`}></i>
                    <input
                      type="number"
                      min="0"
                      value={val ?? ""}
                      onChange={(e) => handleEntityLimitChange(key, e.target.value)}
                      className={`w-full text-center px-2 py-1.5 rounded-lg border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50`}
                      placeholder="∞"
                    />
                    <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>{label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* === SUBMIT === */}
          <div className={`sticky bottom-0 flex items-center justify-end gap-3 pt-4 border-t ${themeClasses.border}`}>
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium ${themeClasses.buttonText} ${themeClasses.buttonHover} transition-all`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loadingSubmit || (isEdit && !hasFormChanges())}
              className="px-6 py-2.5 rounded-xl text-sm font-medium bg-primary-500 text-gray-900 hover:bg-primary-600 transition-all shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loadingSubmit && (
                <div className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin"></div>
              )}
              {isEdit ? "Guardar Cambios" : "Crear Licencia"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
